import React, { FunctionComponent, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { PaginationProps } from 'ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'src/ui/atoms/translate';
import {
  AdminApi,
  AdminApiConnection,
  AdminApiConnectionsListOrderingEnum,
  AdminApiConnectionsListTypeEnum,
  AdminApiConnectionsValuesRetrieveFieldEnum,
  AdminApiConnectionUpdate,
  ApiTypeEnum,
} from 'src/api';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import useApiCall from 'src/hooks/use-api-call';
import CopyButton from 'src/ui/molecules/copy-button';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import EditApiConnectionModal from '../edit-api-connection-modal';
import { DistributionPlatformDropdownOption } from '..';
import EditApiConnectionSuccessModal from '../edit-api-connection-success-modal';
import DeleteApiConnectionModal from '../delete-api-connection-modal';
import { I18NContext } from 'src/ui/i18n/provider';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import { Search } from 'src/ui/molecules/filtering';

export interface ApiConnectionsTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  apiConnections?: AdminApiConnection[];
  paginationProps?: PaginationProps;
  loading?: boolean;
  setEditApiConnectionModalOpen: (val: AdminApiConnection) => void;
  setDeleteApiConnectionModalOpen: (val: AdminApiConnection) => void;
  userId?: string;
}

const ApiConnectionsTable: FunctionComponent<ApiConnectionsTableProps> = (props) => {
  const {
    className,
    loading,
    ordering,
    onOrderBy,
    apiConnections,
    paginationProps,
    setEditApiConnectionModalOpen,
    setDeleteApiConnectionModalOpen,
    userId,
  } = props;

  const { activeLocale } = useContext(I18NContext);

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const apiConnectionFilterOptions = useMemo(() => {
    return Object.values(AdminApiConnectionsListTypeEnum).map((apiConnection) => ({
      id: apiConnection,
      name: translate(`issuerUserApiConnectionsTable.${snakeToCamelCaseWithNumbers(apiConnection)}`),
    }));
  }, [translate]);

  return (
    <div className={cx('users-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={apiConnections}
        paginationProps={paginationProps}
        loading={loading}
        searchComponent={
          <Search
            input="search"
            defaultField="search"
            label={translate('filters.labels.apiConnectionId')}
            name="search"
          />
        }
        cells={[
          {
            title: <Translate name="issuerUserApiConnectionsTable.application" />,
            render: ({ name }) => name,
            orderingFieldName: AdminApiConnectionsListOrderingEnum.NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'name',
            searchFieldName: AdminApiConnectionsValuesRetrieveFieldEnum.NAME,
            getFilterData: (params: any) =>
              adminApi.adminApiConnectionsValuesRetrieve({ ...params, userId: [props.userId] }),
          },
          {
            title: <Translate name="issuerUserApiConnectionsTable.type" />,
            render: ({ apiType }) => (
              <Translate
                name={`issuerUserApiConnectionsTable.${
                  apiType === ApiTypeEnum.ISSUER_API ? 'issuerApi' : 'distributorApi'
                }`}
              />
            ),
            orderingFieldName: AdminApiConnectionsListOrderingEnum.API_TYPE_ASC,
            type: CellTypeEnum.text,
            filterName: 'type',
            getFilterData: () => {
              return apiConnectionFilterOptions;
            },
          },
          {
            title: <Translate name="issuerUserApiConnectionsTable.clientId" />,
            render: ({ clientId }) => clientId,
            copyValue: ({ clientId }) => <CopyButton valueToCopy={clientId} />,
            orderingFieldName: AdminApiConnectionsListOrderingEnum.CLIENT_ID_ASC,
            type: CellTypeEnum.text,
            filterName: 'clientId',
            searchFieldName: AdminApiConnectionsValuesRetrieveFieldEnum.CLIENT_ID,
            getFilterData: (params: any) =>
              adminApi.adminApiConnectionsValuesRetrieve({ ...params, userId: [props.userId] }),
          },
          {
            title: <Translate name="issuerUserApiConnectionsTable.actions" />,
            render: (apiConnection) => (
              <ActionButtons align="center" spacingTop="none">
                <Button variant="link" size="xsmall" onClick={() => setDeleteApiConnectionModalOpen(apiConnection)}>
                  <Translate name="common.delete" />
                </Button>
                <Button variant="secondary" size="xsmall" onClick={() => setEditApiConnectionModalOpen(apiConnection)}>
                  <Translate name="common.edit" />
                </Button>
              </ActionButtons>
            ),
            width: activeLocale === 'de' ? 196 : 140,
          },
        ]}
      />
    </div>
  );
};

interface IssuerUserApiConnectionsTableProps {
  title?: ReactNode;
  filters?: ReactNode;
  forceDataLoad: boolean;
  setForceDataLoad: (val: boolean) => void;
  userId: string;
  setHasIssuerApiConnection: (val: boolean) => void;
  setHasDistributorApiConnection: (val: boolean) => void;
  distributionPlatforms: DistributionPlatformDropdownOption[];
  setEditedApiConnectionWithSecret: (value: AdminApiConnectionUpdate) => void;
  setDeleteApiConnectionSuccessModalOpen: (value: boolean) => void;
  skipFilterUrlSync?: boolean;
}

const IssuerUserApiConnectionsTable: FunctionComponent<IssuerUserApiConnectionsTableProps> = WithDataRecord<
  AdminApi,
  AdminApiConnection
>(AdminApi)(
  ({
    data,
    ordering,
    onOrderBy,
    loading,
    setForceDataLoad,
    paginationProps,
    setHasIssuerApiConnection,
    setHasDistributorApiConnection,
    distributionPlatforms,
    setEditedApiConnectionWithSecret,
    setDeleteApiConnectionSuccessModalOpen,
    userId,
  }) => {
    const [editApiConnectionModalOpen, setEditApiConnectionModalOpen] = useState<AdminApiConnection | undefined>();
    const [deleteApiConnectionModalOpen, setDeleteApiConnectionModalOpen] = useState<AdminApiConnection | undefined>();
    const [editedApiConnectionNoSecret, setEditedApiConnectionNoSecret] = useState<AdminApiConnectionUpdate>();

    useEffect(() => {
      if (data?.length) {
        setHasIssuerApiConnection(data.some((connection) => connection.apiType === ApiTypeEnum.ISSUER_API));
        setHasDistributorApiConnection(data.some((connection) => connection.apiType === ApiTypeEnum.DISTRIBUTOR_API));
      } else {
        setHasIssuerApiConnection(false);
        setHasDistributorApiConnection(false);
      }
    }, [data?.length, deleteApiConnectionModalOpen]);

    return (
      <>
        <ApiConnectionsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          apiConnections={data}
          loading={loading}
          paginationProps={paginationProps}
          setEditApiConnectionModalOpen={setEditApiConnectionModalOpen}
          setDeleteApiConnectionModalOpen={setDeleteApiConnectionModalOpen}
          userId={userId}
        />
        {editApiConnectionModalOpen && (
          <EditApiConnectionModal
            apiConnection={editApiConnectionModalOpen}
            distributionPlatforms={distributionPlatforms}
            onSaveApiConnection={(editedApiConnection, regenerateClientSecret) => {
              setForceDataLoad(true);
              setEditApiConnectionModalOpen(undefined);
              if (regenerateClientSecret) {
                setEditedApiConnectionWithSecret(editedApiConnection);
              } else {
                setEditedApiConnectionNoSecret(editedApiConnection);
              }
            }}
            onClose={() => setEditApiConnectionModalOpen(undefined)}
          />
        )}
        {editedApiConnectionNoSecret && (
          <EditApiConnectionSuccessModal
            onClose={() => setEditedApiConnectionNoSecret(undefined)}
            editedApiConnection={editedApiConnectionNoSecret}
          />
        )}
        {deleteApiConnectionModalOpen && (
          <DeleteApiConnectionModal
            onDelete={() => {
              setForceDataLoad(true);
              setDeleteApiConnectionModalOpen(undefined);
              setDeleteApiConnectionSuccessModalOpen(true);
            }}
            onClose={() => setDeleteApiConnectionModalOpen(undefined)}
            apiConnection={deleteApiConnectionModalOpen}
          />
        )}
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminApiConnectionsList({
      offset,
      limit,
      ordering: ordering as AdminApiConnectionsListOrderingEnum,
      ...props,
      userId: [props.userId],
    });
  },
);

export default IssuerUserApiConnectionsTable;
